/**
 * Opt-in live catalog: a signed-in Pro/Premium viewer can drop `/public` and
 * send a Clerk JWT. Module state so `fetchWithRetry` can read it without
 * becoming a React hook. The footer switch owns the preference; entitlement
 * comes from billing usage.
 */

export type CatalogTier = 'snapshot' | 'live';

export type CatalogLiveSnapshot = {
  live: boolean;
  entitled: boolean;
  enabled: boolean;
  /** True while Live is on and a signed-in billing read still owns the hold. */
  pending: boolean;
  /** True once a signed-in org and billing read have finished. Signed-out stays false. */
  settled: boolean;
};

type LiveSession = {
  entitled: boolean;
  enabled: boolean;
  getToken: (() => Promise<string | null>) | null;
};

const TOGGLE_KEY = 'adj-catalog-live';

const listeners = new Set<() => void>();

function readEnabled(): boolean {
  try {
    return window.localStorage.getItem(TOGGLE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeEnabled(enabled: boolean): void {
  try {
    if (enabled) window.localStorage.setItem(TOGGLE_KEY, '1');
    else window.localStorage.removeItem(TOGGLE_KEY);
  } catch {
    // quota / private mode: preference stays in-memory for this tab
  }
}

let session: LiveSession = {
  entitled: false,
  enabled: typeof window !== 'undefined' ? readEnabled() : false,
  getToken: null,
};

let cached: CatalogLiveSnapshot = {
  live: false,
  entitled: false,
  enabled: session.enabled,
  pending: false,
  settled: false,
};

// A refresh with the footer switch already on must not fire the public price
// reads. Those wait until entitlement is applied. The gate is armed once per
// enabled session; embeds never arm it.
let liveHold: Promise<void> | null = null;
let releaseLiveHold: (() => void) | null = null;
let liveHoldArmed = false;
let entitlementKnown = false;

// Reads that already passed catalogLiveReady. The first entitlement result
// skips revalidation when this is still zero, because those reads resume on
// the resolved tier. A later sign-in, after a finished signed-out read, still
// revalidates. Arming a new hold does not clear the count.
let passedGate = 0;

// withPublicCatalog depth. Raised only while a callback starts its reads, then
// lowered before those reads await. A snapshot chart walk captures the depth
// at that start and stays on the public URL for its own pages when the module
// tier flips. Other catalog reads that start afterward keep the live tier.
let publicCatalogDepth = 0;

export function armCatalogLiveWait(): void {
  // The hook calls this on every Clerk render. Turning Live off clears
  // liveHoldArmed so an unanswered session can wait again; a known session
  // must not open a hold, because nothing will release it.
  if (!session.enabled || liveHoldArmed || entitlementKnown) return;
  liveHoldArmed = true;
  liveHold = new Promise(resolve => {
    releaseLiveHold = resolve;
  });
  // CatalogLiveAuth renders before App, so the chart reads this snapshot in
  // the same pass. Notifying here would setState during that render.
  const next = takeSnapshot();
  if (!sameSnapshot(cached, next)) cached = next;
}

export function catalogLiveReady(): Promise<void> {
  return liveHold ?? Promise.resolve();
}

function releaseCatalogLiveWait(): void {
  const release = releaseLiveHold;
  releaseLiveHold = null;
  liveHold = null;
  release?.();
}

function takeSnapshot(): CatalogLiveSnapshot {
  return {
    live: catalogLiveTier() === 'live',
    entitled: session.entitled,
    enabled: session.enabled,
    // Signed-out releases the hold, so a stored switch does not leave the
    // chart waiting on billing that will not run.
    pending: session.enabled && !entitlementKnown && liveHold != null,
    settled: entitlementKnown,
  };
}

function sameSnapshot(a: CatalogLiveSnapshot, b: CatalogLiveSnapshot): boolean {
  return (
    a.live === b.live &&
    a.entitled === b.entitled &&
    a.enabled === b.enabled &&
    a.pending === b.pending &&
    a.settled === b.settled
  );
}

function emit(): void {
  const next = takeSnapshot();
  if (sameSnapshot(cached, next)) return;
  cached = next;
  listeners.forEach(fn => fn());
}

export function subscribeCatalogLive(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function getCatalogLiveSnapshot(): CatalogLiveSnapshot {
  return cached;
}

export function setCatalogLiveSession(next: Pick<LiveSession, 'entitled' | 'getToken'>): void {
  entitlementKnown = true;
  session = { ...session, entitled: next.entitled, getToken: next.getToken };
  releaseCatalogLiveWait();
  emit();
}

/** Sign-out. The next sign-in can arm the wait again. A signed-out page does not wait. */
export function forgetCatalogLiveEntitlement(): void {
  entitlementKnown = false;
  liveHoldArmed = false;
  session = { ...session, entitled: false, getToken: null };
  releaseCatalogLiveWait();
  emit();
}

export function setCatalogLiveEnabled(enabled: boolean): void {
  writeEnabled(enabled);
  session = { ...session, enabled };
  if (!enabled) {
    liveHoldArmed = false;
    releaseCatalogLiveWait();
  } else if (!entitlementKnown) {
    armCatalogLiveWait();
  }
  const next = takeSnapshot();
  if (!sameSnapshot(cached, next)) cached = next;
  // armCatalogLiveWait already wrote the cached snapshot without notifying.
  listeners.forEach(fn => fn());
}

export function catalogLiveTier(): CatalogTier {
  return session.entitled && session.enabled ? 'live' : 'snapshot';
}

/** Drops the live session and the entitlement gate so a test starts cold. */
export function resetCatalogLiveForTests(): void {
  releaseCatalogLiveWait();
  liveHoldArmed = false;
  entitlementKnown = false;
  passedGate = 0;
  publicCatalogDepth = 0;
  writeEnabled(false);
  session = { entitled: false, enabled: false, getToken: null };
  emit();
}

export function noteCatalogReadPassedGate(): void {
  passedGate += 1;
}

export function catalogReadsPassedGate(): number {
  return passedGate;
}

export function catalogReadsArePublic(): boolean {
  return publicCatalogDepth > 0;
}

/** Run a catalog read against the public URL, whatever the module tier is.
 * The callback's reads capture this scope synchronously as they start. The
 * scope is lowered before they await, so each read keeps the choice it
 * captured and a later catalog read is unaffected. */
export function withPublicCatalog<T>(fn: () => Promise<T>): Promise<T> {
  publicCatalogDepth += 1;
  try {
    return fn();
  } finally {
    publicCatalogDepth -= 1;
  }
}

/**
 * Whether resolving entitlement should revalidate tier-following SWR keys.
 * A first result with no read past the gate must not: those reads resume on
 * the tier just applied. Leaving live, or signing in after a finished
 * snapshot read, still revalidates.
 */
export function entitlementShouldRevalidate(
  wasLive: boolean,
  nowLive: boolean,
  readsPassedGate: number
): boolean {
  if (wasLive) return true;
  return nowLive && readsPassedGate > 0;
}

export async function catalogLiveToken(): Promise<string | null> {
  if (catalogLiveTier() !== 'live' || !session.getToken) return null;
  try {
    return await session.getToken();
  } catch {
    return null;
  }
}

/**
 * Paths that stay on the public snapshot when Live is on.
 *
 * Index and rate lists are the caller's full registry, including rows the
 * public snapshot does not publish. The events list is the markets page, and
 * its status, venue, volume, open-interest, expiry, and category filters
 * exist only on the public route. News and filings teasers are three rows;
 * their live twins are embedding searches.
 */
const PUBLIC_TEASER = /^\/(indices|markets|rates)\/[^/]+\/(news|filings)$/;

export function keepPublicCatalogPath(publicPathAndQuery: string): boolean {
  const path = publicPathAndQuery.split('?')[0] ?? publicPathAndQuery;
  if (path === '/plans' || path === '/meta') return true;
  if (path === '/events') return true;
  if (path === '/indices' || path === '/rates') return true;
  if (PUBLIC_TEASER.test(path)) return true;
  return false;
}

/**
 * SWR keys to revalidate when the catalog tier flips.
 * Public-URL keys are excluded. Chart and daily keys carry the tier in the
 * key, so a tier change subscribes a new key instead of revalidating the old
 * one against the new URL.
 */
export function catalogSwrKeyFollowsTier(key: unknown): boolean {
  if (
    key === 'snapshot-meta' ||
    key === 'public-indices-list' ||
    key === 'indices' ||
    key === 'rates-list'
  ) {
    return false;
  }
  if (!Array.isArray(key)) return true;
  const parts = key as readonly unknown[];
  const head = parts[0];
  if (
    head === 'indices-page' ||
    head === 'rates-page' ||
    head === 'related-news' ||
    head === 'related-filings' ||
    head === 'full-history-chart' ||
    head === 'chart-fine-history' ||
    head === 'chart-fine-tip' ||
    head === 'entity-daily-series'
  ) {
    return false;
  }
  if (head === 'catalog-page' && parts[1] === 'events') return false;
  return true;
}

/** Rewrite `/api/v1/public/...` to `/api/v1/...` when the live session is on. */
export function liveCatalogUrl(url: string): string | null {
  const marker = '/api/v1/public';
  const idx = url.indexOf(marker);
  if (idx < 0) return null;
  const rest = url.slice(idx + marker.length);
  if (keepPublicCatalogPath(rest)) return null;
  return `${url.slice(0, idx)}/api/v1${rest}`;
}
